import { useMutation, useQuery } from '@apollo/client';
import { Icon as SnapUIIcOn } from '@teamsnap/snap-ui';
import { Button, Icon } from '@teamsnap/teamsnap-ui';
import RemoveUser from 'core/gql/mutations/delete-support-admin-role';
import * as ShowOrganization from 'core/gql/queries/show-organizations';
import { ROUTES_LIST } from 'core/routes';
import { redirectToTeamsnapTeamDashboard } from 'core/utils';
import { useAmplitude } from 'hooks/amplitude';
import * as React from 'react';
import { matchPath, useHistory } from 'react-router';
import {
  ACCOUNTS_URL,
  CLASSIC_ADMIN_URL,
  TEAMSNAP_URL,
} from '../core/constants';
import * as ShowUser from '../core/gql/queries/show-user';
import BreadcrumbsWithRouter from './breadcrumbs-with-router';
import './notifications/launch-notes.scss';
import NavigationSearch from './search/navigation-search';
import './toolbar.scss';

type Props = {
  Link: React.FunctionComponent<{ to: string }>;
  orgId: string;
  hideOnMobile?: boolean;
  showNavigationSearch?: boolean;
};

enum Menu {
  None,
  Account,
  Admin,
  Help,
  Notifications,
}

const dashboardUrl = `${TEAMSNAP_URL}/team/dashboard`;

const OrgToolbar = ({
  Link,
  orgId,
  hideOnMobile,
  showNavigationSearch,
}: Props) => {
  const { amplitudeTrack } = useAmplitude();
  const isMobileDevice = window.innerWidth <= 480;
  const [removeUser] = useMutation(RemoveUser);

  const queryOrganization = useQuery(ShowOrganization.BASIC_ORG_QUERY, {
    variables: { organizationId: +orgId },
  });

  const removeFromOrg = () => {
    removeUser({
      variables: { orgId: orgId || undefined },
    }).then(() => {
      redirectToTeamsnapTeamDashboard();
    });
  };

  const showUser = useQuery(ShowUser.QUERY);
  const user = showUser.data?.user;
  const isAdmin = user?.isAdmin;

  const currentOrg = queryOrganization?.data?.showOrganization;

  const accountBody = (
    <>
      <div style={{ minWidth: '100px' }} className="u-padSm">
        <div>
          {user?.firstName} {user?.lastName}
        </div>
        <div className="u-colorNeutral8 u-fontSizeXs">{user?.email}</div>
      </div>
      <div className="u-padSm toolbar-item">
        <a href={dashboardUrl}>TeamSnap Dashboard</a>
      </div>
      <div className="u-padSm toolbar-item">
        <a href={`${ACCOUNTS_URL}`}>My Account</a>
      </div>
      <div className="u-padSm toolbar-item">
        <a href={`${TEAMSNAP_URL}/account?view=clubs`}>Billing and Plans</a>
      </div>
      <div
        className="u-padSm toolbar-item"
        style={{ borderTop: '1px solid #ccc' }}
      >
        <a href={TEAMSNAP_URL + '/login/signout'}>Sign Out</a>
      </div>
    </>
  );
  const adminBody = (
    <div style={{ minWidth: '100px' }}>
      <div className="toolbar-item u-padSm">
        <a href={`${CLASSIC_ADMIN_URL}`}>Go to Admin</a>
      </div>
      <div className="toolbar-item u-padSm">
        <Link to="/employee/organizations">Employee Portal</Link>
      </div>
      <div className="toolbar-item u-padSm">
        <button onClick={removeFromOrg}>Remove from Org</button>
      </div>
    </div>
  );

  const history = useHistory();
  let match = null;
  ROUTES_LIST.find(
    (route) =>
      (match = matchPath(history.location.pathname, {
        path: route,
        exact: true,
      }))
  );
  const pathParams = match?.params || {};
  const searchContext = {
    organizationId: +pathParams['orgId'],
    programId: +pathParams['programId'],
    seasonId: +pathParams['seasonId'],
    memberId: +pathParams['memberId'],
  };

  const btnStyle = { color: '#000', marginTop: '-5px' };
  const iconStyle = {
    border: '1px solid black',
    borderRadius: '12px',
    padding: '4px',
    width: '24px',
    height: '24px',
    fontWeight: 'bold',
  };

  const [activeMenu, setActiveMenu] = React.useState(Menu.None);

  React.useEffect(() => {
    const listener = (e) => {
      const isTargetingPopup = e.target.closest('.flyout-container') != null;

      if (!isTargetingPopup) {
        setActiveMenu(Menu.None);
      }
    };
    document.body.addEventListener('click', listener);

    return () => {
      document.body.removeEventListener('click', listener);
    };
  });

  const goToHelp = () => {
    window.open(
      'https://helpme.teamsnap.com/category/1824-teamsnap-for-business',
      '_blank'
    );
    amplitudeTrack.tsbHelpLinkClicked({
      organization_id: orgId,
      organization_name: currentOrg.name,
      page_viewed_url: window.location.href,
    });
  };

  return hideOnMobile && isMobileDevice ? null : (
    <div className="Grid Toolbar">
      <BreadcrumbsWithRouter user={user} />
      {showNavigationSearch && (
        <NavigationSearch searchContext={searchContext} />
      )}
      <div className="Grid-cell u-sizeFit u-flex u-flexAlignContentCenter u-flexJustifyEnd u-padXs flyout-container">
        <div>
          <Button type="link" style={btnStyle} onClick={() => goToHelp()}>
            <SnapUIIcOn
              className="Icon sui-ml-2"
              size="small"
              name="question_mark"
              style={iconStyle}
            />
          </Button>
        </div>
        <div>
          <Button
            type="link"
            style={btnStyle}
            onClick={() => setActiveMenu(Menu.Account)}
          >
            <Icon className="Icon sui-ml-2" name="user" style={iconStyle} />
          </Button>
          {activeMenu === Menu.Account && accountBody && (
            <div className="u-posAbsolute Toolbar-flyout">{accountBody}</div>
          )}
        </div>
        {isAdmin && (
          <div>
            <Button
              type="link"
              style={btnStyle}
              onClick={() => setActiveMenu(Menu.Admin)}
            >
              <Icon className="Icon sui-ml-2" name="wrench" style={iconStyle} />
            </Button>
            {activeMenu === Menu.Admin && adminBody && (
              <div className="u-posAbsolute Toolbar-flyout">{adminBody}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

OrgToolbar.defaultProps = {
  hideOnMobile: false,
  showNavigationSearch: true,
};

export default OrgToolbar;
